/**
 * Módulo "Haz tu pack" — Bodymania (tema Hyper / FoxTheme).
 *
 * Web Component sin dependencias. Se integra con el tema cuando está
 * disponible (FoxTheme.routes, FoxTheme.utils.fetchConfig,
 * FoxTheme.Currency.formatMoney, pubsub cartUpdate y <cart-drawer>),
 * con fallbacks nativos si no lo está.
 */
if (!customElements.get('haz-tu-pack')) {
  class HazTuPack extends HTMLElement {
    #mainVariants = [];

    connectedCallback() {
      this.cartDrawer = document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[data-htp-submit]');
      this.totalElement = this.querySelector('[data-htp-total]');
      this.totalCompareElement = this.querySelector('[data-htp-total-compare]');
      this.savingsElement = this.querySelector('[data-htp-savings]');
      this.savingsAmountElement = this.querySelector('[data-htp-savings-amount]');
      this.liveRegion = this.querySelector('[data-htp-live]');
      this.errorElement = this.querySelector('.product-form__error-message');

      const variantsJson = this.querySelector('[data-htp-main-variants]');
      if (variantsJson) {
        try {
          this.#mainVariants = JSON.parse(variantsJson.textContent);
        } catch (error) {
          this.#mainVariants = [];
        }
      }

      for (const checkbox of this.querySelectorAll('[data-htp-check]')) {
        checkbox.addEventListener('change', () => this.updateTotals());
      }

      for (const select of this.querySelectorAll('[data-htp-variant-select]')) {
        select.addEventListener('change', (event) => this.#handleVariantChange(event));
      }

      for (const toggle of this.querySelectorAll('[data-htp-alt-toggle]')) {
        toggle.addEventListener('click', (event) => this.#toggleAlternatives(event));
      }

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openPanel = this.querySelector('[data-htp-alt-panel]:not([hidden])');
        if (!openPanel) return;
        this.#closePanel(openPanel, true);
      });

      this.submitButton?.addEventListener('click', () => this.#addToCart());

      // Sincroniza la fila del producto principal cuando cambia la variante
      // en el selector de la ficha (el tema actualiza el input oculto [name=id]).
      this.sectionElement = this.closest('.shopify-section') || document;
      this.sectionElement.addEventListener('change', () => {
        window.setTimeout(() => this.#syncMainVariant(), 400);
      });

      this.updateTotals();
    }

    get slots() {
      return [...this.querySelectorAll('.htp-slot')];
    }

    /** Opción visible (activa) de un slot. */
    #activeOption(slot) {
      return slot.querySelector('.htp-option:not([hidden])');
    }

    /**
     * Precios (en céntimos) de la opción activa de un slot.
     * compare es el compare_at_price de la variante seleccionada (0 si no
     * existe o no es mayor que el precio final).
     */
    #optionPrices(option) {
      const select = option.querySelector('[data-htp-variant-select]');
      if (select) {
        const selected = select.options[select.selectedIndex];
        return {
          price: parseInt(selected.dataset.price, 10) || 0,
          compare: parseInt(selected.dataset.compare, 10) || 0,
        };
      }
      return {
        price: parseInt(option.dataset.price, 10) || 0,
        compare: parseInt(option.dataset.compare, 10) || 0,
      };
    }

    /** Refresca precio, tachado, badge y aria-label de una opción. */
    #refreshOptionPrice(option, price, compare) {
      const priceElement = option.querySelector('[data-htp-price]');
      const compareElement = option.querySelector('[data-htp-compare]');
      const badgeElement = option.querySelector('[data-htp-badge]');
      const wrap = option.querySelector('[data-htp-price-wrap]');
      const hasCompare = compare > price;

      if (priceElement) priceElement.textContent = this.formatMoney(price);
      if (compareElement) {
        compareElement.hidden = !hasCompare;
        compareElement.textContent = hasCompare ? this.formatMoney(compare) : '';
      }
      if (badgeElement) {
        badgeElement.hidden = !hasCompare;
        badgeElement.textContent = hasCompare
          ? `-${Math.round(((compare - price) / compare) * 100)}%`
          : '';
      }
      if (wrap) {
        wrap.setAttribute(
          'aria-label',
          hasCompare
            ? `Precio: ${this.formatMoney(price)}. Precio anterior: ${this.formatMoney(compare)}`
            : `Precio: ${this.formatMoney(price)}`
        );
      }
    }

    /** Variant id a añadir para la opción activa de un slot. */
    #optionVariantId(option) {
      const select = option.querySelector('[data-htp-variant-select]');
      if (select) return parseInt(select.value, 10);
      return parseInt(option.dataset.variantId, 10);
    }

    formatMoney(cents) {
      const theme = window.FoxTheme;
      if (theme?.Currency?.formatMoney && theme?.settings?.moneyFormat) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    }

    updateTotals() {
      let total = 0;
      let totalWithoutDiscount = 0;
      let checkedCount = 0;

      for (const slot of this.slots) {
        const option = this.#activeOption(slot);
        if (!option) continue;
        const checkbox = option.querySelector('[data-htp-check]');
        if (!checkbox?.checked) continue;

        checkedCount += 1;
        const prices = this.#optionPrices(option);
        total += prices.price;
        totalWithoutDiscount += Math.max(prices.compare, prices.price);
      }

      if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);
      if (this.totalCompareElement) {
        this.totalCompareElement.textContent = totalWithoutDiscount > total ? this.formatMoney(totalWithoutDiscount) : '';
      }

      // Ahorro total del pack = suma de (compare - price) de los items marcados
      // con compare > price. Es equivalente a total tachado - total final.
      const savings = totalWithoutDiscount - total;
      this.#updateSavings(savings);

      if (this.submitButton) this.submitButton.disabled = checkedCount === 0;

      let announcement = `Total del pack: ${this.formatMoney(total)}`;
      if (savings > 0) announcement += `. Ahorras ${this.formatMoney(savings)}`;
      this.#announce(announcement);
    }

    /**
     * Muestra u oculta el encabezado de ahorro total y actualiza el importe.
     * Si el ahorro es 0 (nada marcado o ningún item con compare_at), se oculta
     * para no mostrar "ahorra 0,00 €".
     * @param {number} savingsCents - Ahorro total en céntimos.
     */
    #updateSavings(savingsCents) {
      if (!this.savingsElement) return;

      const hasSavings = savingsCents > 0;
      this.savingsElement.hidden = !hasSavings;

      if (this.savingsAmountElement) {
        this.savingsAmountElement.textContent = hasSavings ? this.formatMoney(savingsCents) : '';
      }
    }

    #handleVariantChange(event) {
      const select = event.target;
      const option = select.closest('.htp-option');
      const selected = select.options[select.selectedIndex];
      this.#refreshOptionPrice(
        option,
        parseInt(selected.dataset.price, 10) || 0,
        parseInt(selected.dataset.compare, 10) || 0
      );
      this.updateTotals();
    }

    #syncMainVariant() {
      const input = this.sectionElement.querySelector?.('.main-product-form .product-variant-id');
      if (!input?.value) return;

      const mainOption = this.querySelector('.htp-option[data-role="main"]');
      if (!mainOption || mainOption.dataset.variantId === input.value) return;

      const variant = this.#mainVariants.find((item) => String(item.id) === String(input.value));
      if (!variant) return;

      const compare = variant.compare > variant.price ? variant.compare : 0;
      mainOption.dataset.variantId = String(variant.id);
      mainOption.dataset.price = String(variant.price);
      mainOption.dataset.compare = String(compare);
      this.#refreshOptionPrice(mainOption, variant.price, compare);
      this.updateTotals();
    }

    /* ---------- Alternativas ---------- */

    #toggleAlternatives(event) {
      const toggle = event.currentTarget;
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.#closePanel(panel, false);
        return;
      }

      this.#buildAlternativesList(panel);
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      panel.querySelector('.htp-alt-choice')?.focus();
    }

    #closePanel(panel, restoreFocus) {
      const slot = panel.closest('.htp-slot');
      const toggle = slot?.querySelector('[data-htp-alt-toggle]');
      panel.hidden = true;
      toggle?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) toggle?.focus();
    }

    #buildAlternativesList(panel) {
      const slot = panel.closest('.htp-slot');
      const list = panel.querySelector('[data-htp-alt-list]');
      if (!slot || !list) return;

      list.textContent = '';
      const activeOption = this.#activeOption(slot);

      for (const option of slot.querySelectorAll('.htp-option')) {
        const isActive = option === activeOption;
        const prices = this.#optionPrices(option);

        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'htp-alt-choice';
        button.setAttribute('aria-pressed', String(isActive));

        const image = option.querySelector('.htp-option__img');
        if (image) {
          const thumb = image.cloneNode(false);
          thumb.className = 'htp-alt-choice__img';
          thumb.removeAttribute('sizes');
          button.appendChild(thumb);
        }

        const title = document.createElement('span');
        title.className = 'htp-alt-choice__title';
        title.textContent = option.dataset.title || '';
        button.appendChild(title);

        const price = document.createElement('span');
        price.className = 'htp-alt-choice__price';
        price.textContent = this.formatMoney(prices.price);
        button.appendChild(price);

        if (isActive) {
          const current = document.createElement('span');
          current.className = 'htp-alt-choice__current';
          current.textContent = 'Seleccionado';
          button.appendChild(current);
        }

        button.addEventListener('click', () => this.#selectOption(slot, option, panel));
        item.appendChild(button);
        list.appendChild(item);
      }
    }

    #selectOption(slot, option, panel) {
      const previous = this.#activeOption(slot);
      if (previous && previous !== option) {
        const wasChecked = previous.querySelector('[data-htp-check]')?.checked ?? true;
        previous.hidden = true;
        option.hidden = false;
        const checkbox = option.querySelector('[data-htp-check]');
        if (checkbox) checkbox.checked = wasChecked;
        this.#announce(`${option.dataset.title} seleccionado para el pack`);
      }
      this.#closePanel(panel, true);
      this.updateTotals();
    }

    /* ---------- Añadir al carrito ---------- */

    async #addToCart() {
      const items = [];
      for (const slot of this.slots) {
        const option = this.#activeOption(slot);
        if (!option) continue;
        if (!option.querySelector('[data-htp-check]')?.checked) continue;

        if (option.dataset.role === 'main') this.#syncMainVariant();
        const id = this.#optionVariantId(option);
        if (id) items.push({ id, quantity: 1 });
      }

      if (items.length === 0) return;

      const theme = window.FoxTheme;
      const cartAddUrl = theme?.routes?.cart_add_url || '/cart/add';

      // Sin drawer (o en la página de carrito): envío clásico con redirección.
      if (document.body.classList.contains('cart-template') || (theme?.settings && theme.settings.cartType !== 'drawer')) {
        if (theme?.utils?.postLink) {
          theme.utils.postLink(cartAddUrl, { parameters: { items } });
          return;
        }
      }

      this.#showError(false);
      this.#toggleLoading(true);

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const fetchConfig = theme?.utils?.fetchConfig
        ? theme.utils.fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          };

      try {
        const response = await fetch(cartAddUrl, {
          ...fetchConfig,
          body: JSON.stringify({ items, sections: sectionsToBundle, section_url: window.location.pathname }),
        });
        const parsedState = await response.json();

        if (parsedState.status) {
          this.#showError(parsedState.description || parsedState.message);
          document.dispatchEvent(
            new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } })
          );
          return;
        }

        const cartUrl = theme?.routes?.cart_url || '/cart';
        const cartResponse = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        const cartJson = await cartResponse.json();
        cartJson.sections = parsedState.sections;

        if (theme?.pubsub?.publish && theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        }
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

        if (this.cartDrawer?.show) {
          this.cartDrawer.show(this.submitButton);
        } else {
          this.#announce('Pack añadido al carrito');
        }
      } catch (error) {
        console.error('[haz-tu-pack]', error);
        this.#showError('No se ha podido añadir el pack al carrito. Inténtalo de nuevo.');
      } finally {
        this.#toggleLoading(false);
      }
    }

    #toggleLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.classList.toggle('btn--loading', isLoading);
      if (isLoading) {
        this.submitButton.setAttribute('disabled', 'true');
      } else {
        this.submitButton.removeAttribute('disabled');
      }
    }

    #showError(message = false) {
      if (!this.errorElement) {
        if (message) window.alert(message);
        return;
      }
      this.errorElement.hidden = !message;
      if (message) this.errorElement.textContent = message;
    }

    #announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      requestAnimationFrame(() => {
        this.liveRegion.textContent = message;
      });
    }
  }

  customElements.define('haz-tu-pack', HazTuPack);
}
